import React, { useEffect, useState, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import the styles
import { getData } from "utilities/common";
import { STILO } from "utilities/constants";
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../redux/slices/common";
import Quill from "quill";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";

const CommonQuillEditor = ({
  value,
  disabled = false,
  onChange,
  allowImage = true,
  allowHeading = true,
  style = false,
  height = "400px",
}) => {
  const adminsMode = getData("adminsMode");
  const isStilo = JSON.parse(adminsMode) === STILO;
  const [editorHtml, setEditorHtml] = useState("");
  const dispatch = useDispatch();
  const quillRef = React.useRef(null);

  // Image size validation function
  const validateImageSize = useCallback((file) => {
    const MAX_FILE_SIZE = 500 * 1024; // 500KB
    if (file.size > MAX_FILE_SIZE) {
      //  showToastMsg(`Image size must be less than 500KB. Current size: ${(file.size / 1024).toFixed(2)}KB`, "error", undefined, { persist: true });
      dispatch(
        openSnackbar({
          content: `Image size must be less than 500KB. Current size: ${(file.size / 1024).toFixed(2)}KB`,
          color: "error",
          icon: "warning",
        })
      );
      return false;
    }
    return true;
  }, [dispatch]);

  // Custom image handler for Quill
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      // Validate file size
      if (!validateImageSize(file)) {
        return;
      }

      const formData = new FormData();
      formData.append('filePath', file);

      try {
        // Import uploadImage dynamically to avoid circular dependency
        const { uploadImage } = await import('../../redux/slices/common');
        const result = await dispatch(uploadImage(formData)).unwrap();
        
        if (result?.fileUrl) {
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', result.fileUrl);
          }
        }
      } catch (error) {
        dispatch(
          openSnackbar({
            content: error.message || 'Failed to upload image',
            color: "error",
            icon: "warning"
          })
        );
      }
    };
  }, [dispatch, validateImageSize]);

  // Custom paste handler to validate image size
  const handlePaste = useCallback(async (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    
    if (!clipboardData) {
      return;
    }
    
    const items = clipboardData.items;

    let imageProcessed = false;
    let hasInvalidImage = false;

    // Check for images and validate them first
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check direct image items
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file && !validateImageSize(file)) {
          hasInvalidImage = true;
          break;
        }
      }
      
      // Check HTML content for images
      if (item.type === 'text/html') {
        const html = await new Promise((resolve) => {
          item.getAsString(resolve);
        });
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const images = tempDiv.querySelectorAll('img');
        
        for (let img of images) {
          if (img.src.startsWith('data:image')) {
            try {
              const response = await fetch(img.src);
              const blob = await response.blob();
              const file = new File([blob], 'pasted-image', { type: blob.type });
              
              if (!validateImageSize(file)) {
                hasInvalidImage = true;
                break;
              }
            } catch (error) {
              console.error('Error processing data URL:', error);
            }
          }
        }
        if (hasInvalidImage) break;
      }
    }

    // If there's an invalid image, prevent paste and show error
    if (hasInvalidImage) {
      console.log('Invalid image found, preventing paste');
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Now process the paste - let Quill handle text, we'll handle images
    setTimeout(async () => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Handle direct image items
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          
          if (file) {
            try {
              const formData = new FormData();
              formData.append('filePath', file);
              
              const { uploadImage } = await import('../../redux/slices/common');
              const result = await dispatch(uploadImage(formData)).unwrap();
              
              if (result?.fileUrl) {
                const quill = quillRef.current?.getEditor();
                if (quill) {
                  const range = quill.getSelection(true);
                  quill.insertEmbed(range.index, 'image', result.fileUrl);
                }
                imageProcessed = true;
              }
            } catch (error) {
              console.error('Upload error:', error);
              dispatch(
                openSnackbar({
                  content: error.message || 'Failed to upload pasted image',
                  color: "error",
                  icon: "warning"
                })
              );
            }
          }
          break; // Only process first image
        }
        
        // Handle HTML content with images
        if (item.type === 'text/html' && !imageProcessed) {
          const html = await new Promise((resolve) => {
            item.getAsString(resolve);
          });
          
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          const images = tempDiv.querySelectorAll('img');
          
          if (images.length > 0) {
            // Process only the first image to prevent duplicates
            const img = images[0];
            const src = img.src;
            
            if (src.startsWith('data:image')) {
              try {
                const response = await fetch(src);
                const blob = await response.blob();
                const file = new File([blob], 'pasted-image', { type: blob.type });
                
                const formData = new FormData();
                formData.append('filePath', file);
                
                const { uploadImage } = await import('../../redux/slices/common');
                const result = await dispatch(uploadImage(formData)).unwrap();
                
                if (result?.fileUrl) {
                  const quill = quillRef.current?.getEditor();
                  if (quill) {
                    const range = quill.getSelection(true);
                    quill.insertEmbed(range.index, 'image', result.fileUrl);
                  }
                  imageProcessed = true;
                }
              } catch (error) {
                console.error('Error processing data URL:', error);
                dispatch(
                  openSnackbar({
                    content: error.message || 'Failed to process pasted image',
                    color: "error",
                    icon: "warning"
                  })
                );
              }
            } else if (src.startsWith('http')) {
              const quill = quillRef.current?.getEditor();
              if (quill) {
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', src);
              }
              imageProcessed = true;
            }
          }
          break;
        }
      }
    }, 0);

    // Let Quill handle the paste normally for text content
    console.log('Allowing Quill to handle paste for text content');
  }, [dispatch, validateImageSize]);

  const handleChange = (html) => {
    setEditorHtml(html);
    onChange && onChange(html);
  };

  useEffect(() => {
    setEditorHtml(value);
  }, [value]);

  useEffect(() => {
    const quillEditor = quillRef.current?.getEditor();
    if (quillEditor) {
      const quillRoot = quillEditor.root;
      
      // Add paste event listener with proper capture
      const pasteHandler = (e) => {
        handlePaste(e);
      };
      
      quillRoot.addEventListener('paste', pasteHandler, true);
      
      // Cleanup
      return () => {
        quillRoot.removeEventListener('paste', pasteHandler, true);
      };
    }
  }, [handlePaste]);

  // Custom modules configuration
  const modules = {
    toolbar: {
      container: [
        allowHeading
          ? [{ header: [1, 2, 3, 4, 5, 6, false] }]
          : [{ header: [2, 3, 4, 5, 6, false] }], // Conditionally remove heading
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }],
        allowImage ? ["link", "image"] : ["link"],
        ["clean"],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ align: [] }],
      ],
      handlers: allowImage ? {
        image: imageHandler,
      } : {},
    },
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <div className="mb-4">
      <ReactQuill
        ref={quillRef}
        className="quill-text-editor"
        theme="snow"
        value={editorHtml}
        readOnly={disabled}
        onChange={handleChange}
        modules={modules}
        formats={[
          'header', 'font', 'size',
          'bold', 'italic', 'underline', 'strike', 'blockquote',
          'list', 'bullet', 'indent',
          'link', 'image',
          'color', 'background',
          'align'
        ]}
        style={
          isStilo && style
            ? {
                height: height,
                display: "flex",
                flexDirection: "column",
              }
            : undefined
        }
      />
    </div>
  );
};

export default CommonQuillEditor;
